// Master only — admin_users CRUD + ses erişimi yönetimi.
// create-user audit'i kendi yazıyor → frontend'den user.create yollamıyoruz.
// set_user_voices audit'i RPC içinde yazıyor → frontend'den tekrar yazma.

import { useEffect, useState } from 'react';
import { supabase, type AdminUser, type VoiceRow } from '../lib/supabase';
import { logAdminAction } from '../lib/audit';
import { useAuth } from '../lib/auth-context';
import { Plus, Shield, ShieldOff, AlertTriangle, Loader2, X, Mic2 } from 'lucide-react';
import VoiceAccessModal from '../components/VoiceAccessModal';

interface CreateUserPayload {
  email: string;
  display_name: string;
  is_master: boolean;
  notes: string;
  password: string;
}

interface CreateUserResponse {
  auth_uid: string;
  admin_user_id: string;
  email: string;
  invite_sent: boolean;
}

function translateCreateError(code: string, message: string): string {
  if (code === 'forbidden' || message.includes('master_admin_only')) return 'Bu işlem sadece master admin tarafından yapılabilir.';
  if (message.includes('user_exists') || code === '409') return 'Bu e-posta zaten kayıtlı.';
  if (message.includes('invalid_email') || code === '422') return 'Geçersiz e-posta adresi.';
  if (message.includes('auth_create_failed')) return 'Auth tarafında kullanıcı oluşturulamadı. Supabase loglarını kontrol et.';
  return message;
}

function translateDeactivateError(msg: string): string {
  if (msg.includes('cannot_deactivate_self')) return 'Kendi hesabını pasifleyemezsin.';
  if (msg.includes('master_admin_only')) return 'Bu işlem sadece master admin tarafından yapılabilir.';
  return msg;
}

function translateVoiceAccessError(msg: string): string {
  if (msg.includes('master_admin_only')) return 'Bu işlem sadece master admin tarafından yapılabilir.';
  if (msg.includes('user_not_found')) return 'Kullanıcı bulunamadı veya pasif durumda.';
  if (msg.includes('cannot_restrict_self')) return 'Kendi ses erişimini kısıtlayamazsın.';
  return msg;
}

export default function Users() {
  const auth = useAuth();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  // Yeni kullanıcı modal
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState<CreateUserPayload>({
    email: '',
    display_name: '',
    is_master: false,
    notes: '',
    password: '',
  });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [formSuccess, setFormSuccess] = useState<string | null>(null);
  const [deactivatingEmail, setDeactivatingEmail] = useState<string | null>(null);

  // Ses erişimi modal
  const [voiceModalUser, setVoiceModalUser] = useState<AdminUser | null>(null);
  const [allVoices, setAllVoices] = useState<VoiceRow[]>([]);
  const [selectedSlugs, setSelectedSlugs] = useState<Set<string>>(new Set());
  const [voiceModalLoading, setVoiceModalLoading] = useState(false);
  const [voiceSaving, setVoiceSaving] = useState(false);
  const [voiceError, setVoiceError] = useState<string | null>(null);

  const loadUsers = () => {
    setLoading(true);
    void supabase
      .from('admin_users')
      .select('*')
      .order('created_at', { ascending: false })
      .then(({ data, error }) => {
        if (error) setListError(error.message);
        setUsers((data ?? []) as AdminUser[]);
        setLoading(false);
      });
  };

  useEffect(() => { loadUsers(); }, []);

  // ─── Yeni kullanıcı modal ──────────────────────────────────────────────────

  function openModal() {
    setForm({ email: '', display_name: '', is_master: false, notes: '', password: '' });
    setFormError(null);
    setFormSuccess(null);
    setModalOpen(true);
  }

  function closeModal() {
    setModalOpen(false);
    setFormError(null);
    setFormSuccess(null);
  }

  function setField<K extends keyof CreateUserPayload>(key: K, value: CreateUserPayload[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleCreate() {
    if (!form.email.trim()) {
      setFormError('E-posta zorunlu.');
      return;
    }
    setSaving(true);
    setFormError(null);
    setFormSuccess(null);

    const { data, error } = await supabase.functions.invoke<CreateUserResponse>('create-user', {
      body: {
        email: form.email.trim().toLowerCase(),
        display_name: form.display_name.trim() || null,
        is_master: form.is_master,
        notes: form.notes.trim() || null,
        password: form.password.trim() || null,
      },
    });

    if (error) {
      const errData = data as unknown as { error?: string; detail?: string } | null;
      const code = errData?.error ?? '';
      const detail = errData?.detail ?? error.message;
      setFormError(translateCreateError(code, detail));
      setSaving(false);
      return;
    }

    const note = form.password.trim()
      ? ' Verdiğin şifreyle giriş yapabilir.'
      : ' Şifre verilmedi — master, Supabase Auth panelinden şifre atamalı.';
    setFormSuccess(`Kullanıcı oluşturuldu.${note}`);
    setSaving(false);
    loadUsers();
    setTimeout(() => closeModal(), 1200);
  }

  // ─── Deactivate ───────────────────────────────────────────────────────────

  async function handleDeactivate(user: AdminUser) {
    if (!window.confirm(`"${user.email}" pasifleştirilsin mi? Auth tarafına dokunulmaz.`)) return;
    setDeactivatingEmail(user.email);
    setListError(null);

    const { error } = await supabase.rpc('deactivate_admin_user', { p_email: user.email });

    if (error) {
      setListError(translateDeactivateError(error.message));
      setDeactivatingEmail(null);
      return;
    }

    await logAdminAction({
      action: 'user.deactivate',
      target_type: 'admin_user',
      target_label: user.email,
      metadata: { target_id: user.id },
    });

    setDeactivatingEmail(null);
    loadUsers();
  }

  // ─── Ses erişimi modal ────────────────────────────────────────────────────

  async function openVoiceModal(user: AdminUser) {
    setVoiceModalUser(user);
    setVoiceError(null);
    setVoiceModalLoading(true);

    // Tüm aktif voice profilleri + bu kullanıcının mevcut atamaları paralel çek
    const [vpRes, uvaRes] = await Promise.all([
      supabase
        .from('voice_profiles')
        .select('id, slug, display_name, category, is_active, display_order')
        .eq('is_active', true)
        .order('display_order'),
      supabase
        .from('user_voice_access')
        .select('voice_profile_id')
        .eq('admin_user_id', user.id),
    ]);

    if (vpRes.error) {
      setVoiceError(vpRes.error.message);
      setVoiceModalLoading(false);
      return;
    }

    const vpRows = (vpRes.data ?? []) as VoiceRow[];
    setAllVoices(vpRows);

    type UvaRow = { voice_profile_id: string };
    const uvaRows = (uvaRes.data ?? []) as UvaRow[];
    const assigned = new Set(
      uvaRows
        .map((r) => {
          // uuid → slug eşleştirmesi
          const vp = vpRows.find((v) => v.id === r.voice_profile_id);
          return vp?.slug ?? '';
        })
        .filter((s): s is string => s.length > 0)
    );

    setSelectedSlugs(assigned);
    setVoiceModalLoading(false);
  }

  function closeVoiceModal() {
    setVoiceModalUser(null);
    setAllVoices([]);
    setSelectedSlugs(new Set());
    setVoiceError(null);
  }

  function toggleSlug(slug: string) {
    setSelectedSlugs((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) {
        next.delete(slug);
      } else {
        next.add(slug);
      }
      return next;
    });
  }

  async function handleSaveVoiceAccess() {
    if (!voiceModalUser) return;
    setVoiceSaving(true);
    setVoiceError(null);

    const { error } = await supabase.rpc('set_user_voices', {
      p_email: voiceModalUser.email,
      p_voice_slugs: Array.from(selectedSlugs),
    });

    if (error) {
      setVoiceError(translateVoiceAccessError(error.message));
      setVoiceSaving(false);
      return;
    }

    setVoiceSaving(false);
    closeVoiceModal();
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-4xl">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Kullanıcılar</h1>
          <p className="text-slate-500 text-sm">Master admin ve editör hesapları.</p>
        </div>
        <button
          onClick={openModal}
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-soft)] text-white rounded-lg text-sm font-medium shadow-sm"
        >
          <Plus size={14} /> Yeni kullanıcı
        </button>
      </div>

      {listError && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 flex items-start gap-2">
          <AlertTriangle size={15} className="shrink-0 mt-0.5" />
          <span>{listError}</span>
        </div>
      )}

      {loading ? (
        <p className="text-slate-400">Yükleniyor…</p>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
          <table className="w-full text-sm min-w-[680px]">
            <thead className="bg-slate-50 text-xs uppercase text-slate-600">
              <tr>
                <th className="px-4 py-3 text-left">E-posta</th>
                <th className="px-4 py-3 text-left">İsim</th>
                <th className="px-4 py-3 text-left">Rol</th>
                <th className="px-4 py-3 text-left">Aktif</th>
                <th className="px-4 py-3 text-left">Oluşturuldu</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {users.map((u) => {
                const isSelf = u.email.toLowerCase() === auth.email?.toLowerCase();
                const isDeactivating = deactivatingEmail === u.email;
                const showVoiceBtn = !u.is_master && u.is_active;
                return (
                  <tr key={u.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-slate-800 font-mono text-xs">{u.email}</td>
                    <td className="px-4 py-3 text-slate-700">{u.display_name ?? '—'}</td>
                    <td className="px-4 py-3">
                      {u.is_master ? (
                        <span className="inline-flex items-center gap-1 text-xs bg-amber-50 text-amber-800 px-2 py-0.5 rounded">
                          <Shield size={11} /> master
                        </span>
                      ) : (
                        <span className="text-xs text-slate-500">editör</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {u.is_active ? (
                        <span className="text-xs text-emerald-700">Aktif</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                          <ShieldOff size={11} /> pasif
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-xs">
                      {new Date(u.created_at).toLocaleDateString('tr-TR')}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-3">
                        {showVoiceBtn && (
                          <button
                            onClick={() => void openVoiceModal(u)}
                            title="Ses erişimini düzenle"
                            className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:underline"
                          >
                            <Mic2 size={11} /> Sesler
                          </button>
                        )}
                        {u.is_active && (
                          <button
                            onClick={() => void handleDeactivate(u)}
                            disabled={isSelf || isDeactivating}
                            title={isSelf ? 'Kendi hesabını pasifleyemezsin' : 'Pasifle'}
                            className="inline-flex items-center gap-1 text-xs text-red-600 hover:underline disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            {isDeactivating && <Loader2 size={11} className="animate-spin" />}
                            Pasifle
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="mt-4 text-xs text-slate-400">
        Not: "Yeni kullanıcı" hem Supabase Auth hesabını hem admin_users satırını oluşturur. Şifre vermezsen master, Supabase Auth panelinden şifre atamalı (davet maili gönderilmez).
      </p>

      {/* ─── Yeni kullanıcı modal ─────────────────────────────────────────── */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-slate-100">
              <h2 className="font-semibold text-slate-800">Yeni kullanıcı</h2>
              <button onClick={closeModal} className="p-1 rounded hover:bg-slate-100 text-slate-500">
                <X size={18} />
              </button>
            </div>

            <div className="px-6 py-5 space-y-4">
              {/* Email */}
              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5 block">
                  E-posta <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setField('email', e.target.value)}
                  placeholder="editor@example.com"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--color-primary)]"
                />
              </div>

              {/* Display name */}
              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5 block">
                  Görünen ad
                </label>
                <input
                  type="text"
                  value={form.display_name}
                  onChange={(e) => setField('display_name', e.target.value)}
                  placeholder="Ali Yılmaz"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--color-primary)]"
                />
              </div>

              {/* Password */}
              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5 block">
                  Şifre
                </label>
                <input
                  type="password"
                  value={form.password}
                  onChange={(e) => setField('password', e.target.value)}
                  placeholder="Boş bırakırsan master sonra atar"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--color-primary)]"
                />
                <p className="text-xs text-slate-400 mt-1">
                  Boş bırakırsan kullanıcı oluşur ama şifresi olmaz; master Supabase Auth panelinden atar. Davet maili gönderilmez.
                </p>
              </div>

              {/* is_master */}
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Master admin</span>
                <button
                  type="button"
                  onClick={() => setField('is_master', !form.is_master)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${form.is_master ? 'bg-[color:var(--color-primary)]' : 'bg-slate-300'}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${form.is_master ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>

              {/* Notes */}
              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5 block">
                  Not
                </label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setField('notes', e.target.value)}
                  rows={2}
                  placeholder="Opsiyonel not…"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--color-primary)] resize-none"
                />
              </div>

              {formError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 flex items-start gap-2">
                  <AlertTriangle size={15} className="shrink-0 mt-0.5" />
                  <span>{formError}</span>
                </div>
              )}
              {formSuccess && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-700">
                  {formSuccess}
                </div>
              )}
            </div>

            <div className="px-6 pb-5 flex justify-end gap-2">
              <button
                onClick={closeModal}
                disabled={saving}
                className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 disabled:opacity-40"
              >
                Vazgeç
              </button>
              <button
                onClick={() => void handleCreate()}
                disabled={saving}
                className="inline-flex items-center gap-2 px-4 py-2 bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-soft)] text-white rounded-lg text-sm font-medium disabled:opacity-40"
              >
                {saving && <Loader2 size={14} className="animate-spin" />}
                {saving ? 'Oluşturuluyor…' : 'Oluştur'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Ses erişimi modal ────────────────────────────────────────────── */}
      {voiceModalUser !== null && (
        <VoiceAccessModal
          user={voiceModalUser}
          allVoices={allVoices}
          selectedSlugs={selectedSlugs}
          loading={voiceModalLoading}
          saving={voiceSaving}
          error={voiceError}
          onToggle={toggleSlug}
          onSave={() => void handleSaveVoiceAccess()}
          onClose={closeVoiceModal}
        />
      )}
    </div>
  );
}
